import { useEffect, useState } from "react";
import { Header } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Loader2, Users, FolderKanban, DollarSign, FileText, TrendingUp, Target, ArrowUp, ArrowDown } from "lucide-react";
import {
  Tooltip as ShadTooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../components/ui/tooltip";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const CHART_COLORS = ["#0F4C81", "#00A3E0", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#06B6D4"];

export default function Analytics() {
  const [overview, setOverview] = useState(null);
  const [pipelineData, setPipelineData] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [revenueTrends, setRevenueTrends] = useState([]);
  const [quoteAnalytics, setQuoteAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/analytics/overview`).then(r => setOverview(r.data)),
      axios.get(`${API_URL}/analytics/pipeline`).then(r => setPipelineData(r.data)),
      axios.get(`${API_URL}/analytics/lead-sources`).then(r => setLeadSources(r.data)),
      axios.get(`${API_URL}/analytics/service-types`).then(r => setServiceTypes(r.data)),
      axios.get(`${API_URL}/analytics/revenue-trends`).then(r => setRevenueTrends(r.data)),
      axios.get(`${API_URL}/analytics/quotes`).then(r => setQuoteAnalytics(r.data)),
    ]).catch(console.error).finally(() => setLoading(false));
  }, []);

  const formatCurrency = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(v || 0);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" /></div>;
  }

  return (
    <div className="min-h-screen">
      <Header title="Analytics & Reports" />

      <div className="p-4 sm:p-6 space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {[
            { label: "Total Revenue", value: formatCurrency(overview?.total_revenue), icon: DollarSign, bg: "bg-emerald-50", iconColor: "text-emerald-600", testId: "total-revenue", desc: "Sum of all approved/signed quote values across all projects" },
            { label: "Projects", value: overview?.total_projects || 0, icon: FolderKanban, bg: "bg-blue-50", iconColor: "text-blue-600", testId: "total-projects", desc: "Total number of projects in your pipeline across all categories" },
            { label: "Contacts", value: overview?.total_contacts || 0, icon: Users, bg: "bg-purple-50", iconColor: "text-purple-600", desc: "Total contacts (customers, leads, vendors) in your CRM" },
            { label: "Conversion Rate", value: `${overview?.conversion_rate || 0}%`, icon: Target, bg: "bg-amber-50", iconColor: "text-amber-600", desc: "Percentage of quotes sent that were approved/signed by customers" },
            { label: "Avg Job Value", value: formatCurrency(overview?.avg_job_value), icon: TrendingUp, bg: "bg-cyan-50", iconColor: "text-cyan-600", desc: "Average dollar value per approved project" },
          ].map(kpi => (
            <TooltipProvider key={kpi.label} delayDuration={200}>
              <ShadTooltip>
                <TooltipTrigger asChild>
                  <Card className="cursor-help">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm text-slate-500">{kpi.label}</p>
                          <p className="text-2xl font-bold text-slate-800 mt-1" data-testid={kpi.testId}>{kpi.value}</p>
                        </div>
                        <div className={`p-2 ${kpi.bg} rounded-lg`}><kpi.icon className={`w-5 h-5 ${kpi.iconColor}`} /></div>
                      </div>
                    </CardContent>
                  </Card>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-xs"><p>{kpi.desc}</p></TooltipContent>
              </ShadTooltip>
            </TooltipProvider>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pipeline Distribution */}
          <Card>
            <CardHeader>
              <TooltipProvider delayDuration={200}><ShadTooltip><TooltipTrigger asChild>
                <CardTitle className="text-base cursor-help border-b border-dashed border-slate-300 inline-block">Pipeline Distribution</CardTitle>
              </TooltipTrigger><TooltipContent className="max-w-xs text-xs"><p>Number of projects in each pipeline stage — shows where your projects are concentrated</p></TooltipContent></ShadTooltip></TooltipProvider>
              <CardDescription>Projects by stage</CardDescription>
            </CardHeader>
            <CardContent>
              {pipelineData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={pipelineData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} angle={-30} textAnchor="end" height={80} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid #E2E8F0" }} />
                    <Bar dataKey="count" name="Projects">
                      {pipelineData.map((entry, i) => (
                        <Cell key={i} fill={entry.color || CHART_COLORS[i % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-400">No pipeline data</div>
              )}
            </CardContent>
          </Card>

          {/* Lead Sources */}
          <Card>
            <CardHeader>
              <TooltipProvider delayDuration={200}><ShadTooltip><TooltipTrigger asChild>
                <CardTitle className="text-base cursor-help border-b border-dashed border-slate-300 inline-block">Lead Sources</CardTitle>
              </TooltipTrigger><TooltipContent className="max-w-xs text-xs"><p>Breakdown of where your contacts/leads originated — helps you see which marketing channels perform best</p></TooltipContent></ShadTooltip></TooltipProvider>
              <CardDescription>Where your leads come from</CardDescription>
            </CardHeader>
            <CardContent>
              {leadSources.length > 0 ? (
                <div className="flex items-center gap-6">
                  <ResponsiveContainer width="50%" height={250}>
                    <PieChart>
                      <Pie data={leadSources} dataKey="leads" nameKey="source" cx="50%" cy="50%" outerRadius={90} innerRadius={50}>
                        {leadSources.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-2">
                    {leadSources.map((source, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                        <span className="flex-1 text-slate-600 capitalize">{source.source?.replace(/_/g, " ") || "Unknown"}</span>
                        <span className="font-medium text-slate-700">{source.leads}</span>
                        <span className="text-slate-400 text-xs">({source.conversion_rate}%)</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-400">No lead source data</div>
              )}
            </CardContent>
          </Card>

          {/* Revenue Trends */}
          <Card>
            <CardHeader>
              <TooltipProvider delayDuration={200}><ShadTooltip><TooltipTrigger asChild>
                <CardTitle className="text-base cursor-help border-b border-dashed border-slate-300 inline-block">Revenue Trends</CardTitle>
              </TooltipTrigger><TooltipContent className="max-w-xs text-xs"><p>Monthly comparison of total pipeline value vs completed/paid revenue over time</p></TooltipContent></ShadTooltip></TooltipProvider>
              <CardDescription>Monthly pipeline and completed revenue</CardDescription>
            </CardHeader>
            <CardContent>
              {revenueTrends.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={revenueTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="_id" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} formatter={(v) => [`$${v.toLocaleString()}`, ""]} />
                    <Line type="monotone" dataKey="total_value" stroke="#0F4C81" strokeWidth={2} name="Pipeline" dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="completed_value" stroke="#10B981" strokeWidth={2} name="Completed" dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-400">No trend data</div>
              )}
            </CardContent>
          </Card>

          {/* Service Types */}
          <Card>
            <CardHeader>
              <TooltipProvider delayDuration={200}><ShadTooltip><TooltipTrigger asChild>
                <CardTitle className="text-base cursor-help border-b border-dashed border-slate-300 inline-block">Services Breakdown</CardTitle>
              </TooltipTrigger><TooltipContent className="max-w-xs text-xs"><p>Revenue and project count broken down by service type (Roofing, Fencing, etc.)</p></TooltipContent></ShadTooltip></TooltipProvider>
              <CardDescription>Revenue and projects by service type</CardDescription>
            </CardHeader>
            <CardContent>
              {serviceTypes.length > 0 ? (
                <div className="space-y-4">
                  {serviceTypes.map((service, i) => {
                    const maxValue = Math.max(...serviceTypes.map(s => s.total_value));
                    const widthPercent = maxValue > 0 ? (service.total_value / maxValue) * 100 : 0;
                    return (
                      <div key={i} className="space-y-1">
                        <div className="flex justify-between items-center text-sm">
                          <span className="font-medium text-slate-700 capitalize">{service.service?.replace(/_/g, " ") || "Unknown"}</span>
                          <span className="text-slate-500">{service.projects} projects | {formatCurrency(service.total_value)}</span>
                        </div>
                        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${widthPercent}%`, backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-slate-400">No service data</div>
              )}
            </CardContent>
          </Card>

          {/* Quote Stats */}
          {quoteAnalytics && (
            <Card className="lg:col-span-2">
              <CardHeader>
                <TooltipProvider delayDuration={200}><ShadTooltip><TooltipTrigger asChild>
                  <CardTitle className="text-base cursor-help border-b border-dashed border-slate-300 inline-block">Quote Analytics</CardTitle>
                </TooltipTrigger><TooltipContent className="max-w-xs text-xs"><p>Quote performance breakdown by status — see how many quotes are draft, sent, approved, or rejected and their total values</p></TooltipContent></ShadTooltip></TooltipProvider>
                <CardDescription>Quote performance by status</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {quoteAnalytics.by_status?.map((stat, i) => {
                    const statusDescs = {
                      draft: "Quotes still being built, not yet sent to customers",
                      sent: "Quotes delivered to customers awaiting review",
                      approved: "Quotes signed and approved by customers",
                      rejected: "Quotes declined or expired",
                      viewed: "Quotes opened/viewed by the customer",
                    };
                    const statusKey = (stat._id || "").toLowerCase().replace(/\s/g, "_");
                    return (
                      <TooltipProvider key={i} delayDuration={200}>
                        <ShadTooltip>
                          <TooltipTrigger asChild>
                            <div className="p-4 bg-slate-50 rounded-lg cursor-help">
                              <p className="text-sm text-slate-500 capitalize">{stat._id?.replace(/_/g, " ") || "Unknown"}</p>
                              <p className="text-2xl font-bold text-slate-800 mt-1">{stat.count}</p>
                              <p className="text-sm text-slate-400">{formatCurrency(stat.total_value)}</p>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs text-xs"><p>{statusDescs[statusKey] || `Quotes with status: ${stat._id}`}</p></TooltipContent>
                        </ShadTooltip>
                      </TooltipProvider>
                    );
                  })}
                </div>
                {quoteAnalytics.monthly_trends?.length > 0 && (
                  <div className="mt-6">
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={quoteAnalytics.monthly_trends}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="_id" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                        <Bar dataKey="count" fill="#0F4C81" name="Quotes" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="approved" fill="#10B981" name="Approved" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
